import { useQuery, useQueryClient } from '@tanstack/react-query';
import { useCallback } from 'react';

import type { OpcaoDeSelecao } from '@/componentes/Campo';
import { iniciaisDoNome } from '@/componentes/lista-de-escolha';
import { useSessaoEmMemoria } from '@/modulos/sessao/memoria';

import { pedir } from './api';
import { lerEquipamento, type EquipamentoDaFrota } from './frota';
import { iconeDoTipo, opcaoDaMaquina } from './opcao-da-maquina';

/* A máquina como opção mora em `opcao-da-maquina.ts` (pura, com teste); daqui
   ela segue exportada para quem já a importava deste arquivo. */
export { iconeDoTipo, opcaoDaMaquina };

/**
 * A FOTO, O ÍCONE E O AVATAR NAS LISTAS DE ESCOLHA (pedido do dono, 20/10/2026:
 * "onde tenha foto, coloque a foto no dropdown").
 *
 * - MÁQUINA: a miniatura que a lista da frota já entrega (`foto.miniaturaUrl`,
 *   32 px, assinada por 5 minutos). A tela não pede foto nenhuma a mais: a
 *   imagem só é baixada quando a lista abre e a opção aparece (`loading=lazy`,
 *   e a lista fechada nem existe no DOM). Sem foto, ou com a URL vencida, o
 *   ícone do tipo.
 * - MODELO e CONFIGURAÇÃO não têm foto: o ícone do tipo da máquina.
 * - PESSOA não tem foto no sistema: as iniciais no avatar (p.24).
 */

/** O modelo do catálogo: fabricante e nome, o tipo embaixo, o ícone do tipo. */
export function opcaoDoModelo(m: {
  readonly id: string;
  readonly nome: string;
  readonly fabricante?: string | null;
  readonly tipo?: string | null;
}): OpcaoDeSelecao {
  return {
    valor: m.id,
    rotulo: [m.fabricante, m.nome].filter(Boolean).join(' '),
    ...(m.tipo ? { detalhe: m.tipo } : {}),
    icone: iconeDoTipo(m.tipo),
  };
}

/** A configuração: o rótulo que a tela já escreve, o tipo embaixo e o ícone do tipo (ou do medidor). */
export function opcaoDaConfiguracao(
  c: { readonly id: string; readonly tipo?: string | null; readonly tipoMedidor?: string | null },
  rotulo: string,
): OpcaoDeSelecao {
  return {
    valor: c.id,
    rotulo,
    ...(c.tipo ? { detalhe: c.tipo } : {}),
    icone: iconeDoTipo(c.tipo, c.tipoMedidor),
  };
}

/** A pessoa: o nome, as iniciais no avatar e, quando houver, a segunda linha (função, e-mail). */
export function opcaoDaPessoa(p: { readonly id: string; readonly nome: string }, detalhe?: string | null): OpcaoDeSelecao {
  return {
    valor: p.id,
    rotulo: p.nome,
    iniciais: iniciaisDoNome(p.nome),
    ...(detalhe ? { detalhe } : {}),
  };
}

/** A URL da miniatura vale 5 minutos; a lista é pedida de novo antes disso. */
const VALIDADE_DAS_URLS_MS = 4 * 60_000;

/**
 * Para a tela cuja lista de máquinas vem de OUTRA rota (cadastros do
 * combustível, opções do orçamento…), que traz id, código e descrição mas não
 * a foto: completa cada opção com a foto, o ícone do tipo e a segunda linha a
 * partir da lista da frota.
 *
 * - Só pede a frota quem a lê (`equipamento.ler`); sem a permissão, as opções
 *   ficam como vieram, sem aviso de falha.
 * - Aproveita a frota já em cache (a página da frota, a ficha) quando ela é
 *   recente; senão pede UMA vez a lista, e só as URLs — nenhuma imagem é
 *   baixada até a lista abrir.
 * - A falha é calada: sem a frota, a lista continua escolhendo, só sem foto.
 */
export function useOpcoesComVisualDaFrota(ligada = true): (opcoes: readonly OpcaoDeSelecao[]) => readonly OpcaoDeSelecao[] {
  const sessao = useSessaoEmMemoria();
  const cliente = useQueryClient();
  /* A pessoa de posto parceiro (0132) tem `equipamento.ler` para a escolha da
     máquina no lançamento, mas a lista da frota (`/equipamentos`) o servidor
     recusa a ela: a escolha segue com o código e o tipo, sem a foto. */
  const pode = (sessao?.permissoes.includes('equipamento.ler') ?? false) && sessao?.parceiro === undefined;

  const consulta = useQuery({
    queryKey: ['frota', 'visual-da-escolha'] as const,
    enabled: ligada && pode,
    staleTime: VALIDADE_DAS_URLS_MS,
    meta: { silenciarFalha: true, oQueLia: 'as fotos da frota' },
    queryFn: async (): Promise<ReadonlyMap<string, OpcaoDeSelecao>> => {
      const emCache = cliente.getQueryState<readonly EquipamentoDaFrota[]>(['frota']);
      const recente = emCache?.data !== undefined && Date.now() - emCache.dataUpdatedAt < VALIDADE_DAS_URLS_MS;
      const frota = recente && emCache.data
        ? emCache.data
        : (await pedir<{ equipamentos: Record<string, unknown>[] }>('/equipamentos')).equipamentos.map(lerEquipamento);
      return new Map(frota.map((m) => [m.id, opcaoDaMaquina(m)]));
    },
  });

  const porId = consulta.data;
  return useCallback(
    (opcoes: readonly OpcaoDeSelecao[]) =>
      porId === undefined
        ? opcoes
        : opcoes.map((o) => {
            const daFrota = porId.get(o.valor);
            if (!daFrota) return o;
            /* O rótulo da tela fica (é ele que a pessoa conhece); a frota
               acrescenta o que falta: a foto, o ícone, a segunda linha e as
               etiquetas do código interno e da placa, quando a rota da tela
               não as trouxe. */
            return {
              ...daFrota,
              ...o,
              ...(o.detalhe === undefined && daFrota.detalhe !== undefined ? { detalhe: daFrota.detalhe } : {}),
              ...(o.etiquetas === undefined && daFrota.etiquetas !== undefined ? { etiquetas: daFrota.etiquetas } : {}),
            };
          }),
    [porId],
  );
}
